import React, { useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useNavigation, useCustomer, useConfig } from 'msp-integrations'
import {
  Grid,
  DynamicTag,
  AlertBox,
  Spinner,
  Button,
  Field,
  PageActionPlaceHolder
} from '../../../index'
import {
  useRegisterField,
  cleanPrefixPhone,
  removeInitialZero,
  getPrefixPhone
} from './AccountData.helpers'
import { PasswordChangeForm } from './PasswordChangeForm'
import {
  messages,
  validateBirthDay
} from '@organisms/RegisterForm/RegisterForm.utils'

const AccountData = () => {
  const fieldConfig = useRegisterField()
  const navigate = useNavigation()
  const alertBoxRef = useRef<typeof AlertBox>()
  const [alertBoxMessage, setAlertBoxMessage] = useState({
    type: 'error',
    message: ''
  })
  const [phoneHomePrefix, setPhoneHomePrefix] = useState('')
  const [phoneBusinessPrefix, setPhoneBusinessPrefix] = useState('')
  const [phoneMobilePrefix, setPhoneMobilePrefix] = useState('')
  const { formatMessage } = useIntl()
  const {
    theme: { font },
    info: { team, supportEmail }
  } = useConfig()
  const customer = useCustomer()
  const {
    updateCustomer,
    updatePassword,
    salutation,
    firstName,
    lastName,
    birthday,
    email,
    phoneHome,
    phoneMobile,
    phoneBusiness
  } = customer
  const { control, errors, formState, handleSubmit, setError, clearErrors } =
    useForm({
      shouldUnregister: false,
      defaultValues: {
        salutation,
        firstName,
        lastName,
        birthday,
        email,
        phoneHome: cleanPrefixPhone(phoneHome),
        phoneMobile: cleanPrefixPhone(phoneMobile),
        phoneBusiness: cleanPrefixPhone(phoneBusiness)
      }
    })

  const onFormSubmit = async (data: any) => {
    const body = { ...data }
    Object.keys(body).forEach((key) => {
      if (!body[key]) {
        delete body[key]
      }
    })

    if (data.birthday) {
      const isValidBirthdate = validateBirthDay(data.birthday)

      if (!isValidBirthdate) {
        setError(fieldConfig.birthday.name, {
          type: 'manual',
          message: formatMessage(messages.invalid)
        })
        return
      }
    }

    const { phoneHome, phoneMobile, phoneBusiness } = body
    body.phoneMobile =
      phoneMobile &&
      phoneMobilePrefix.concat(removeInitialZero(cleanPrefixPhone(phoneMobile)))
    body.phoneHome =
      phoneHome &&
      phoneHomePrefix.concat(removeInitialZero(cleanPrefixPhone(phoneHome)))
    body.phoneBusiness =
      phoneBusiness &&
      phoneBusinessPrefix.concat(
        removeInitialZero(cleanPrefixPhone(phoneBusiness))
      )

    try {
      await updateCustomer(body)
      setAlertBoxMessage({
        type: 'success',
        message: formatMessage({
          id: 'msp.account.data.updated',
          defaultMessage: 'Your data has been updated successfully.'
        })
      })
      setTimeout(() => navigate('/account'), 2000)
    } catch {
      setAlertBoxMessage({
        type: 'error',
        message: formatMessage({
          id: 'msp.account.data.updated.error',
          defaultMessage:
            'There was an error updating your data. Please, try again.'
        })
      })
    } finally {
      alertBoxRef?.current?.showAlert()
    }
  }

  return (
    <>
      <AlertBox
        readOnly
        ref={alertBoxRef}
        type={alertBoxMessage.type}
        title={alertBoxMessage.message}
      />
      <div>
        <DynamicTag
          componentTagName='Headline'
          tag='h6'
          className={`${font.medium} mb-4 text-black`}
        >
          {formatMessage({
            id: 'msp.account.mydata.title',
            defaultMessage: 'My data'
          })}
        </DynamicTag>
        <DynamicTag
          componentTagName='Headline'
          tag='p'
          className={`${font.medium} mb-4 text-black`}
        >
          {formatMessage({
            id: 'msp.account.mydata.personal',
            defaultMessage: 'Personal data'
          })}
        </DynamicTag>
        <form
          id={'register-form'}
          className='mb-8'
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div className='mb-2'>
            <Field
              control={control}
              config={fieldConfig.salutation}
              error={errors?.salutation}
              isDisabled={formState.isSubmitting}
            />
          </div>
          <Grid wrapperStyles='grid-cols-1 md:grid-cols-2 md:gap-2'>
            <Field
              className={'w-full'}
              control={control}
              config={fieldConfig.firstName}
              error={errors?.firstName}
              isDisabled={formState.isSubmitting}
            />
            <Field
              className={'w-full'}
              control={control}
              config={fieldConfig.lastName}
              error={errors?.lastName}
              isDisabled={formState.isSubmitting}
            />
          </Grid>
          <Grid wrapperStyles='grid-cols-1 md:grid-cols-2 md:gap-2'>
            <Field
              className={'w-full'}
              control={control}
              config={fieldConfig.birthday}
              error={errors?.birthday}
              isDisabled={formState.isSubmitting}
              additionalProps={{ max: '9999-12-31' }}
            />
            <Field
              className={'w-full'}
              control={control}
              config={{
                ...fieldConfig.phoneMobile,
                onUpdatePrefix: (value) => setPhoneMobilePrefix(value),
                initialPrefix: getPrefixPhone(phoneMobile)
              }}
              error={errors?.phoneMobile}
              isDisabled={formState.isSubmitting}
            />
          </Grid>
          <Grid wrapperStyles='grid-cols-1 md:grid-cols-2 md:gap-2'>
            <Field
              className={'w-full'}
              control={control}
              config={{
                ...fieldConfig.phoneHome,
                onUpdatePrefix: (value) => setPhoneHomePrefix(value),
                initialPrefix: getPrefixPhone(phoneHome)
              }}
              error={errors?.phoneHome}
              isDisabled={formState.isSubmitting}
            />
            <Field
              className={'w-full'}
              control={control}
              config={{
                ...fieldConfig.phoneBusiness,
                onUpdatePrefix: (value) => setPhoneBusinessPrefix(value),
                initialPrefix: getPrefixPhone(phoneBusiness)
              }}
              error={errors?.phoneBusiness}
              isDisabled={formState.isSubmitting}
            />
          </Grid>
          <Field
            className={'w-full'}
            control={control}
            config={fieldConfig.email}
            error={errors?.email}
            isDisabled={formState.isSubmitting}
          />
          <Button
            className='my-4 w-full lg:w-6/12'
            onClick={() => clearErrors()}
            iconName={!formState.isSubmitting ? 'ArrowRight' : ''}
            iconPosition='right'
            isDisabled={formState.isSubmitting}
            isLoading={formState.isSubmitting}
            isSubmit
            loadingContent={<Spinner size='xl' />}
          >
            {formatMessage({
              defaultMessage: 'Save changes',
              id: 'msp.account.mydata.save.changes'
            })}
          </Button>
        </form>
        <PasswordChangeForm updatePassword={updatePassword} email={email} />
        <PageActionPlaceHolder
          text={formatMessage(
            {
              id: 'msp.account.mydata.hint',
              defaultMessage:
                'You can delete your customer account at any time. Simply send an email to our {team} at {supportEmail} or use our contact form.'
            },
            {
              team,
              supportEmail
            }
          )}
        />
      </div>
    </>
  )
}

export default AccountData
